import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../ui/Modal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5005/api/';
const KZT = new Intl.NumberFormat('ru-KZ', { maximumFractionDigits: 2 });

const getPaymentStatusText = (status) => {
  if (status === 1) return 'Оплачен';
  if (status === 2) return 'Ожидает оплаты';
  return '—';
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    timeZone: 'Europe/Moscow',
  });
};

function StatusBadge({ name }) {
  const n = String(name || '').toLowerCase();
  let tone = 'neutral';
  if (/ожидает|новая|новый/.test(n))           tone = 'warning';
  else if (/подтверждена|готов|оплачен|доставлен|закрыт|завершен/.test(n)) tone = 'success';
  else if (/отмен|возврат|отклон/.test(n))     tone = 'danger';
  else if (/в пути|производ|собира/.test(n))   tone = 'info';
  return <span className={`crmpage__status crmpage__status--${tone}`}>{name || '—'}</span>;
}

export default function MyOrdersProductPositions({
  isOpen, orders, onClose, orderId, fallbackHeader = {},
}) {
  const [items, setItems] = useState([]);
  const [header, setHeader] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('—');
  const [loading, setLoading] = useState(false);

  // Поддерживаем два режима использования:
  // - { orderId, fallbackHeader } — тянет позиции по id
  // - { orders } — массив записей order_view (старый интерфейс)
  const ordersMode = !orderId && Array.isArray(orders);

  const totalAmount = useMemo(
    () => items.reduce((sum, it) => sum + (Number(it.qty) || 0) * (Number(it.price) || 0), 0),
    [items]
  );

  useEffect(() => {
    if (ordersMode) {
      const list = orders.map((o, i) => ({
        id: i,
        name: o.product_name || o['Товар'] || o.name || '—',
        unit: o.measure_name || o['Ед. изм'] || '',
        qty: Number(o.quantity ?? o['Количество'] ?? 0),
        price: Number(o.price ?? o['Цена'] ?? 0),
      })).filter(it => it.qty > 0);
      setItems(list);
      const f = orders[0] || {};
      setHeader({
        order_number: f.order_number ?? '—',
        client_name: f.client_name ?? '—',
        contract_info: f.contract_info ?? '',
        delivery_address: f.delivery_address ?? '—',
        order_date: f.order_date,
        delivery_date: f.delivery_date,
        status_name: f.status_name ?? '—',
      });
      setPaymentStatus(getPaymentStatusText(f.id_payment_status));
      return;
    }
    if (!isOpen || !orderId) return;
    let abort = false;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const r1 = await fetch(`${API_BASE.replace(/\/$/, '')}/v1/order_orderitems/${orderId}`);
        if (!r1.ok) throw new Error(`order_orderitems ${r1.status}`);
        const rows = await r1.json();
        if (!abort) {
          const mapped = (Array.isArray(rows) ? rows : []).map((row) => ({
            id: row.id_orderitems,
            name: row['Товар'],
            qty: Number(row['Количество'] ?? 0),
            price: Number(row['Цена'] ?? 0),
            unit: row['Ед. изм'] || '',
          })).filter(it => it.qty > 0);
          setItems(mapped);
          const h0 = rows && rows[0] ? rows[0] : {};
          setHeader({
            order_number: fallbackHeader.order_number ?? '—',
            client_name: fallbackHeader.client_name ?? h0['Имя клиента'] ?? '—',
            contract_info: h0.contract_info ?? '',
            delivery_address: fallbackHeader.delivery_address ?? h0['Адрес доставки'] ?? '—',
            order_date: fallbackHeader.order_date ?? h0['Дата заказа'],
            delivery_date: fallbackHeader.delivery_date ?? h0['Дата доставки (ориентировочная)'],
            status_name: fallbackHeader.status_name ?? h0['Статус'] ?? '—',
          });
        }
        const r2 = await fetch(`${API_BASE.replace(/\/$/, '')}/v1/orders/${orderId}`);
        if (r2.ok) {
          const j = await r2.json();
          if (!abort) setPaymentStatus(getPaymentStatusText(j?.id_payment_status));
        }
      } catch (e) { console.error(e); }
      finally { if (!abort) setLoading(false); }
    };
    fetchAll();
    return () => { abort = true; };
  }, [isOpen, orderId, fallbackHeader, ordersMode, orders]);

  const open = ordersMode ? !!orders : !!isOpen;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={`Заявка №${header?.order_number ?? '—'}`}
      subtitle={header?.client_name}
      size="lg"
    >
      <div className="uimodal-field">
        <span className="uimodal-field__label">Договор</span>
        <span className="uimodal-field__value">{header?.contract_info || '—'}</span>
      </div>
      <div className="uimodal-field">
        <span className="uimodal-field__label">Адрес доставки</span>
        <span className="uimodal-field__value">{header?.delivery_address || '—'}</span>
      </div>

      <div className="uimodal-grid-2">
        <div>
          <span className="uimodal-field__label">Дата заказа</span>
          <span className="uimodal-field__value">{formatDate(header?.order_date)}</span>
        </div>
        <div>
          <span className="uimodal-field__label">Дата доставки</span>
          <span className="uimodal-field__value">{formatDate(header?.delivery_date)}</span>
        </div>
      </div>

      <div className="uimodal-grid-2">
        <div>
          <span className="uimodal-field__label">Статус заказа</span>
          <span className="uimodal-field__value"><StatusBadge name={header?.status_name} /></span>
        </div>
        <div>
          <span className="uimodal-field__label">Статус оплаты</span>
          <span className="uimodal-field__value"><StatusBadge name={paymentStatus} /></span>
        </div>
      </div>

      <table className="uimodal-table">
        <thead>
          <tr>
            <th>Товар</th>
            <th>Кол-во</th>
            <th>Ед.</th>
            <th className="num">Цена</th>
            <th className="num">Сумма</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: '#8C8077' }}>Загрузка…</td></tr>
          ) : items.length > 0 ? items.map((it) => (
            <tr key={it.id}>
              <td>{it.name}</td>
              <td className="num">{it.qty}</td>
              <td>{it.unit}</td>
              <td className="num">{KZT.format(it.price)} ₸</td>
              <td className="num">{KZT.format(it.qty * it.price)} ₸</td>
            </tr>
          )) : (
            <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: '#8C8077' }}>Позиции отсутствуют</td></tr>
          )}
        </tbody>
      </table>

      <div className="uimodal-total">
        <span className="uimodal-total__label">Общая сумма</span>
        <span className="uimodal-total__value">{KZT.format(totalAmount)} ₸</span>
      </div>
    </Modal>
  );
}
